import {ReactNode} from "react";
import {
  Box,
  Breakpoint,
  Chip,
  Dialog,
  Divider,
  Icon,
  Stack,
  SxProps,
  Theme,
  Typography,
  useTheme,
} from "@mui/material";
import {Grid} from "@mui/system";

import {CustomIconButton} from "ui";
import pxToRem from "hooks/usePxToRem";
import {Classifications} from "views/ProcessDetails/Process.types";
import ClientClassificationChip from "components/ClientClassificationChip";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  titleIcon: string;
  children?: ReactNode;
  hideDivider?: boolean;
  maxWidth?: false | Breakpoint;
  subtitle?: string;
  dividerBadge?: string | ReactNode;
  paperWidth?: string;
  titleEndIcon?: ReactNode;
  dataTestId?: string;
  sx?: SxProps<Theme>;
  paperOverflow?: string;
  classificationBadge?: Classifications;
  disableEscapeKeyDown?: boolean;
};

const CustomModal = (props: Props) => {
  const {
    isOpen,
    onClose,
    title,
    titleIcon,
    children,
    hideDivider = false,
    maxWidth,
    subtitle,
    dividerBadge,
    paperWidth,
    titleEndIcon,
    dataTestId,
    sx,
    paperOverflow,
    classificationBadge,
    disableEscapeKeyDown,
  } = props;

  const theme = useTheme();

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      fullWidth
      scroll="paper"
      maxWidth={maxWidth}
      data-testid={dataTestId}
      PaperProps={{
        sx: {width: paperWidth ?? "100%", overflow: paperOverflow ?? "auto"},
      }}
      sx={sx}
      disableEscapeKeyDown={disableEscapeKeyDown}
    >
      <Grid container p={pxToRem(20)}>
        <Grid size={10} display="flex" alignItems={"center"} gap={pxToRem(12)}>
          <Box
            sx={{
              height: pxToRem(36),
              width: pxToRem(36),
              borderRadius: pxToRem(5),
              backgroundColor: theme.palette.primary[50],
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Icon sx={{color: theme.palette.primary[700]}}>{titleIcon}</Icon>
          </Box>

          <Stack alignItems="flex-start">
            <Typography variant="h4">{title}</Typography>
            <Typography
              variant="subtitle2"
              sx={{color: theme.palette.text.secondary}}
            >
              {subtitle}
            </Typography>
          </Stack>
          {titleEndIcon && (
            <Stack
              alignItems="flex-start"
              alignSelf="flex-start"
              ml={pxToRem(-8)}
            >
              {titleEndIcon}
            </Stack>
          )}
        </Grid>
        <Grid
          size={2}
          display="flex"
          justifyContent="flex-end"
          alignItems="center"
        >
          <CustomIconButton iconName="close" onClick={onClose} />
        </Grid>
      </Grid>

      {classificationBadge && (
        <Stack
          sx={{
            p: `0 ${pxToRem(65)}`,
            mt: pxToRem(-10),
            mb: pxToRem(10),
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-start",
          }}
        >
          <ClientClassificationChip classification={classificationBadge} />
        </Stack>
      )}

      <Grid container size={12}>
        <Grid size={12}>
          <Divider sx={{display: hideDivider ? "none" : ""}}>
            {dividerBadge && typeof dividerBadge === "string" ? (
              <Stack display="flex">
                <Chip label={dividerBadge} color="primary" />
              </Stack>
            ) : (
              dividerBadge
            )}
          </Divider>
        </Grid>
      </Grid>

      {children}
    </Dialog>
  );
};

export default CustomModal;
